import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";
import { Spinner } from "../Feedback/Loading";

export const buttonStyles = cva(
  [
    "relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap",
    "rounded-[var(--radius-lg)] font-heading font-semibold tracking-wide",
    "transition-[color,background-color,border-color,box-shadow,transform] duration-300 ease-in-out",
    "focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]",
    "active:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-[1.1em] [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        /** Gold call to action. One per view. */
        primary: [
          "bg-gradient-to-b from-ds-gold-light via-ds-gold-primary to-ds-gold-dark",
          "text-ds-text-inverse shadow-ds-md",
          "hover:shadow-ds-gold hover:brightness-110",
        ],
        /** Black glass with a gold rim for supporting actions. */
        secondary: [
          "border border-ds-gold-primary/45 bg-black/55 backdrop-blur-md",
          "text-ds-gold-light",
          "hover:border-ds-gold-accent/80 hover:bg-ds-gold-primary/10 hover:shadow-[0_0_20px_-6px_rgba(212,175,55,0.55)]",
        ],
        /** Gold outline for secondary actions on busy backgrounds. */
        outline: [
          "border border-ds-gold-primary bg-transparent text-ds-gold-light",
          "hover:bg-ds-gold-primary/10 hover:shadow-ds-gold",
        ],
        /** Low-emphasis text button. */
        ghost: ["bg-transparent text-ds-text-muted", "hover:bg-white/5 hover:text-ds-gold-accent"],
        /** Destructive action. */
        danger: [
          "bg-ds-error text-ds-text-inverse",
          "hover:brightness-110 hover:shadow-[0_0_20px_rgba(239,68,68,0.45)]",
        ],
      },
      size: {
        sm: "h-9 px-3 text-[length:var(--font-size-xs)]",
        md: "h-11 px-5 text-[length:var(--font-size-sm)]",
        lg: "h-14 px-8 text-[length:var(--font-size-base)]",
      },
      fullWidth: { true: "w-full" },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonStyles>["variant"]>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonStyles>["size"]>;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Default "primary". */
  variant?: ButtonVariant;
  /** Default "md" (44px tall, a comfortable touch target). */
  size?: ButtonSize;
  fullWidth?: boolean;
  /** Shows a spinner, disables the button and sets aria-busy. */
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** Render the child element (e.g. a router <Link>) with button styles. */
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      size,
      fullWidth,
      loading = false,
      leftIcon,
      rightIcon,
      asChild = false,
      disabled,
      type,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : "button";
    return (
      <Component
        ref={ref}
        type={asChild ? undefined : (type ?? "button")}
        disabled={asChild ? undefined : disabled || loading}
        aria-disabled={asChild && (disabled || loading) ? true : undefined}
        aria-busy={loading || undefined}
        className={cn(buttonStyles({ variant, size, fullWidth }), className)}
        {...props}
      >
        {loading ? <Spinner size="sm" label={null} /> : leftIcon}
        <Slottable>{children}</Slottable>
        {rightIcon}
      </Component>
    );
  },
);
Button.displayName = "Button";
